import { SectionWrapper } from '@common/components/BookingForms/Section';
import { Button } from '@common/components/Button';
import { TextArea } from '@common/components/FormElements/Textarea';
import Portal from '@common/components/Portal';
import { StickyBarContent } from '@common/components/StickyBar';
import { useTranslations } from '@lang';
import { getDetailRoute } from '@features/rates/helpers/bookingRoutes.helper';
import { useEffect } from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { useRouter } from 'shared-routing';
import { Guest } from 'shared-types/Basket';
import { AddExistingGuests } from '../components/AddExistingGuests';
import { GuestField } from '../components/GuestField';
import { RoomRateFormValues } from '../RoomRateForm.schema';
import { NotesWrapper, StyledDateRangeIndicator } from './GuestDetailsStep/GuestDetailsStep.styles';
import { GuestDetailsStepProps } from './GuestDetailsStep.types';

export const GuestDetailsStep = ({
  basketGuests,
  loggedGuest,
  stickyBarId,
  selectedEntity,
  datePartyData,
  goToNextStep,
  addToBasket,
  metaEntity,
  availableExtras,
}: GuestDetailsStepProps) => {
  const { t } = useTranslations();
  const router = useRouter();
  const {
    register,
    handleSubmit,
    control,
    formState: { isValid, isSubmitting },
    watch,
  } = useFormContext<RoomRateFormValues>();

  const fieldArray = useFieldArray({ control, name: 'guests' });
  const guestValues = watch('guests') ?? [];
  const selectedGuests = guestValues.filter(
    (g) => g.firstName || g.lastName || g.externalSpaId || g.id
  ) as unknown as Guest[];

  useEffect(() => {
    if (selectedEntity) {
      return;
    }
    router.push({
      pathname: getDetailRoute(router.pathname, metaEntity.type),
      query: { id: metaEntity.id },
    });
  }, [selectedEntity, router, metaEntity.type, metaEntity.id]);

  const hasExtras = availableExtras?.length;
  const { adults, children } = datePartyData;

  const onSubmit = async (values: RoomRateFormValues) => {
    if (hasExtras) {
      goToNextStep();
    } else {
      await addToBasket({ guestDetails: values.guests, note: values.note });
      goToNextStep();
    }
  };

  return selectedEntity ? (
    <form noValidate>
      <SectionWrapper>
        <AddExistingGuests
          fieldArray={fieldArray}
          guestValues={guestValues}
          selectedGuests={selectedGuests}
          existingGuests={(basketGuests as Guest[]) ?? []}
          minimumGuests={adults}
          maximumGuests={adults}
          isGenderSpecific={false}
          guest={loggedGuest ?? null}
        />
        <GuestField
          fieldArray={fieldArray}
          minimumGuests={adults}
          maximumGuests={adults}
          withLeadGuest
        />
        {children ? <>{`+${t('basket.children', { children })}`}</> : null}
        <NotesWrapper>
          <TextArea
            labelWidth="150px"
            label={t('rooms.notes')}
            id="additional-notes"
            input={register('note')}
            error={undefined}
          />
        </NotesWrapper>
      </SectionWrapper>
      <Portal selector={`#${stickyBarId}`}>
        <StickyBarContent
          name={metaEntity.title || ''}
          subtitle={
            <StyledDateRangeIndicator
              fromDate={datePartyData.fromDate}
              toDate={datePartyData.toDate}
            />
          }
          displayedPrice={selectedEntity?.pricePerStay || null}
          button={
            <Button
              isPrimary
              type="button"
              onClick={handleSubmit(onSubmit)}
              disabled={!isValid || isSubmitting}
            >
              {t(hasExtras ? 'spa.package.nextStep' : 'product.ctaButton')}
            </Button>
          }
        />
      </Portal>
    </form>
  ) : null;
};
